import { AnimatePresence, motion } from 'framer-motion';
import { Search, Settings, QrCode, Map } from 'lucide-react'; // Add Map icon
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { Link, useLocation } from 'react-router';
import { useSearch } from '@/museum-app/context/search-context'; // Import useSearch

interface TopNavbarProps {
    scrolled: boolean;
    isOnline: boolean;
    hasPendingActions: boolean;
}

// Removed nav item definitions from here

/**
 * TopNavbar component displays the navigation bar at the top of the app
 * - Shows the museum logo and title
 * - Provides navigation links for larger screens
 * - Includes a hamburger menu for mobile screens
 * - Displays online/offline status
 */
const TopNavbar: React.FC<TopNavbarProps> = ({ scrolled, isOnline, hasPendingActions }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const { t } = useTranslation(); // Initialize translation hook
    const { openSearch } = useSearch(); // Get openSearch function

    // Define navigation items using t function (Moved inside component)
    const navItems = [
        { path: '/', label: t('navigation.home', 'Home') },
        { path: '/rooms', label: t('navigation.rooms', 'Rooms') },
        { path: '/exhibits', label: t('navigation.exhibits', 'Exhibits') },
        { path: '/tours', label: t('navigation.tours', 'Tours') },
        { path: '/about', label: t('navigation.about', 'About') },
    ];

    // Define mobile navigation items using t function (Moved inside component)
    const mobileNavItems = [
        // ...navItems,
        // { path: '/scan', label: t('navigation.scanQr', 'Scan QR') },
        { path: '/about', label: t('navigation.about', 'About') },
        // { path: '/settings', label: t('navigation.settings', 'Settings') },
    ];

    // Toggle mobile menu
    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    // Close menu
    const closeMenu = () => {
        setMenuOpen(false);
    };

    // Check if a path is active
    const isActive = (path: string) => {
        return location.pathname === path;
    };

    return (
        <header
            className={`fixed inset-x-0 top-0 z-40 backdrop-blur-md transition-all duration-300 ${
                scrolled ? 'bg-white/90 shadow-sm dark:bg-neutral-900/90' : 'bg-white/50 dark:bg-neutral-900/50'
            }`}
        >
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
                {/* Logo and title section */}
                <div className="flex shrink-0">
                    <Link to="" className="flex items-center gap-2" onClick={closeMenu}>
                        <img src="/images/logo.png" alt={t('navigation.logoAlt', 'SK Museum Logo')} className="h-8 w-auto object-contain" />
                        <span className="hidden text-lg font-semibold sm:block">{t('navigation.museumName', 'SK Museum')}</span>
                    </Link>
                </div>

                {/* Desktop navigation links */}
                <nav className="hidden items-center md:flex">
                    <ul className="flex gap-1">
                        {navItems.map((item) => (
                            <li key={item.path}>
                                <Link
                                    to={item.path}
                                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                                        isActive(item.path)
                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-100'
                                            : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800/50'
                                    }`}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Right side actions */}
                <div className="flex items-center gap-3">
                    {/* Online/Offline indicator */}
                    <div className="relative">
                        <div className={`h-2.5 w-2.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                        {hasPendingActions && (
                            <motion.div
                                className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-blue-500"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                            />
                        )}
                    </div>

                    {/* Language selector removed */}
                    {/* <div className="hidden sm:block">
                        <LanguageSelector variant="dropdown" />
                    </div> */}

                    {/* Search button */}
                    <button
                        onClick={openSearch} // Open search overlay on click
                        className="rounded-full p-2 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800/50"
                        aria-label={t('navigation.searchAriaLabel', 'Open search')} // Add aria-label
                    >
                        <Search size={20} />
                    </button>

                    {/* Map button (Desktop) */}
                    <Link
                        to="/map"
                        className="rounded-full p-2 text-neutral-600 transition-colors hover:bg-neutral-100 md:inline-flex dark:text-neutral-300 dark:hover:bg-neutral-800/50"
                        aria-label={t('navigation.map', 'Museum Map')}
                    >
                        <Map size={20} />
                    </Link>

                    {/* Scan button (Desktop) */}
                    <Link
                        to="/scan"
                        className="hidden rounded-full p-2 text-neutral-600 transition-colors hover:bg-neutral-100 md:inline-flex dark:text-neutral-300 dark:hover:bg-neutral-800/50"
                        aria-label={t('navigation.scanQr', 'Scan QR')}
                    >
                        <QrCode size={20} />
                    </Link>

                    {/* Settings button */}
                    <Link
                        to="/settings"
                        className="rounded-full p-2 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800/50"
                        aria-label={t('navigation.settingsAriaLabel', 'Settings')}
                    >
                        <Settings size={20} />
                    </Link>

                    {/* Mobile menu toggle */}
                    <button
                        className="inline-flex rounded-full p-2 text-neutral-600 transition-colors hover:bg-neutral-100 md:hidden dark:text-neutral-300 dark:hover:bg-neutral-800/50"
                        onClick={toggleMenu}
                        aria-label={t('navigation.toggleMenuAriaLabel', 'Toggle menu')}
                        aria-expanded={menuOpen}
                    >
                        <div className="flex w-5 flex-col gap-[3px]">
                            <motion.span
                                className="block h-0.5 bg-current"
                                animate={{
                                    rotate: menuOpen ? 45 : 0,
                                    translateY: menuOpen ? 4 : 0,
                                }}
                                transition={{ duration: 0.2 }}
                            />
                            <motion.span className="block h-0.5 bg-current" animate={{ opacity: menuOpen ? 0 : 1 }} transition={{ duration: 0.2 }} />
                            <motion.span
                                className="block h-0.5 bg-current"
                                animate={{
                                    rotate: menuOpen ? -45 : 0,
                                    translateY: menuOpen ? -6 : 0,
                                }}
                                transition={{ duration: 0.2 }}
                            />
                        </div>
                    </button>
                </div>
            </div>

            {/* Mobile menu overlay */}
            <AnimatePresence>
                {menuOpen && (
                    <>
                        <motion.div
                            className="absolute inset-x-0 top-16 z-20 overflow-hidden bg-white shadow-lg md:hidden dark:bg-neutral-900"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                                duration: 0.3,
                                ease: [0.04, 0.62, 0.23, 0.98],
                            }}
                        >
                            <nav className="px-4 py-6">
                                <ul className="flex flex-col gap-1">
                                    {mobileNavItems.map((item) => (
                                        <motion.li
                                            key={item.path}
                                            variants={{
                                                hidden: { opacity: 0, x: -20 },
                                                visible: { opacity: 1, x: 0 },
                                            }}
                                            initial="hidden"
                                            animate="visible"
                                            exit="hidden"
                                            transition={{ duration: 0.2, delay: mobileNavItems.indexOf(item) * 0.05 }}
                                        >
                                            <Link
                                                to={item.path}
                                                className={`flex items-center rounded-lg px-4 py-3 text-base font-medium ${
                                                    isActive(item.path)
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-100'
                                                        : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800/50'
                                                }`}
                                                onClick={closeMenu}
                                            >
                                                {item.label}
                                            </Link>
                                        </motion.li>
                                    ))}
                                </ul>

                                {/* Language selector removed from mobile menu */}
                                {/* <div className="mt-4 px-4">
                                    <div className="text-sm font-medium text-neutral-500 mb-2">Language</div>
                                    <LanguageSelector variant="buttons" />
                                </div> */}
                            </nav>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </header>
    );
};

export default TopNavbar;
